import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addData, getAllData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import { useSnackbarContext } from "../../component/SnackbarContext";
import { setLoginData } from "../../redux/Reducers/AuthReducer/authSplice";
import { useTranslation } from "react-i18next";

// Translation keys (resolved at render time via t()).
const STREAK_BADGE_KEYS = {
  3: null,
  7: null,
  10: "streak.dynamicStreakBadgeBestValue",
  30: "streak.dynamicStreakBadgeMaxReward",
};

const STREAK_TAGLINE_KEYS = {
  3: "streak.dynamicStreakTaglineQuickWarmUp",
  7: "streak.dynamicStreakTaglineSteadyGrind",
  10: "streak.dynamicStreakTaglineSeriousRoller",
  30: "streak.dynamicStreakTaglineEliteLegend",
};

const formatNumber = (n) => Number(n || 0).toLocaleString("en-US");

const DynamicStreak = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarContext();
  const { IsLoginData } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [streakPlans, setStreakPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (IsLoginData?.idUser) {
      fetchStreakPlans();
    }
  }, [IsLoginData?.idUser]);

  const fetchStreakPlans = async () => {
    try {
      setLoading(true);
      const url = `${endPoints.api.GET_ALL_STREAK_TYPES}/${IsLoginData?.idUser}`;
      const response = await getAllData(url);
      if (response?.status === "success" && response?.data) {
        const mappedPlans = response.data.map((plan) => ({
          id: plan.id,
          days: plan.streak,
          streakType: plan.id,
          label: t('streak.dynamicStreakPlanLabel', { days: plan.streak }),
          tagline: STREAK_TAGLINE_KEYS[plan.streak]
            ? t(STREAK_TAGLINE_KEYS[plan.streak])
            : t('streak.dynamicStreakTaglineDefault'),
          sc: plan.sc,
          gc: plan.gc,
          spins: plan.freeSpin,
          badge: STREAK_BADGE_KEYS[plan.streak]
            ? t(STREAK_BADGE_KEYS[plan.streak])
            : null,
          features: plan.specifications
            ? plan.specifications.split(".").filter((f) => f.trim())
            : [],
          termsAndConditions: plan.termsAndConditions || "",
        }));
        setStreakPlans(mappedPlans);
        if (mappedPlans.length > 0 && !selectedId) {
          setSelectedId(mappedPlans[1]?.id || mappedPlans[0]?.id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch streak plans:", error);
      showErrorSnackbar(t('streak.dynamicStreakLoadPlansFailed'));
    } finally {
      setLoading(false);
    }
  };

  const selected = streakPlans.find((p) => p.id === selectedId);
  const streakType = IsLoginData?.dailyReward?.streakType;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const GetUserById = async () => {
    try {
      if (IsLoginData?.idUser) {
        const url = `${endPoints.api.GET_BY_USERID}/${IsLoginData?.idUser}`;
        const response = await getAllData(url);
        dispatch(setLoginData({ user: response.data, isAuthenticated: true }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectStreak = async () => {
    if (!IsLoginData?.idUser || !selected) return;
    setSubmitting(true);
    try {
      const payload = {
        userId: IsLoginData.idUser,
        streakType: selected.streakType,
      };
      const response = await addData(endPoints.api.UPDATE_STREAK_TYPE, payload);
      if (response?.data?.status === "success") {
        showSuccessSnackbar(
          response?.data?.message ||
            t('streak.dynamicStreakStarted', { days: selected.days })
        );
        await GetUserById();
        handleClose();
      } else {
        showErrorSnackbar(
          response?.data?.message || t('streak.dynamicStreakStartFailed')
        );
      }
    } catch (error) {
      console.error("Select streak error:", error);
      showErrorSnackbar(t('streak.dynamicStreakStartFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Only offer the streak selection while the user hasn't picked one yet
  // (streakType === 0 or undefined/null). Once updated, this hides automatically.
  if (streakType > 0 || loading || streakPlans.length === 0) return null;

  return (
    <>
      {/* ---------- Static Card (sits in the promotions grid) ---------- */}
      <Grid item xs={6} sm={6} md={3}>
        <Card
          className="c_card middle_card"
          sx={{
            background: theme.palette.custom.lgray,
            borderColor: theme.palette.custom.redblue_border,
          }}
        >
          <div className="buy_card1">
            <div className="right_half">
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocalFireDepartmentRoundedIcon
                  sx={{
                    fontSize: "95px",
                    color: "#ff7a00",
                    filter: "drop-shadow(0 0 20px rgba(255,122,0,0.6))",
                  }}
                />
              </Box>
            </div>
            <div className="left_half">
              <Typography
                className="f18 f-bold"
                sx={{ color: theme.palette.custom.white_gray }}
                style={{ display: "flex", justifyContent: "center" }}
              >
                {t('streak.dynamicStreakTitle')}
              </Typography>

              <Button
                className="best_btn"
                size="small"
                sx={{
                  background: theme.palette.custom.red,
                  color: theme.palette.custom.white,
                  fontSize: "13px!important",
                }}
                onClick={handleOpen}
              >
                {t('streak.dynamicStreakViewPlans')}
              </Button>
            </div>
          </div>
        </Card>
      </Grid>

      {/* ---------- Plans Dialog ---------- */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 10101 }}
        PaperProps={{
          sx: {
            background: "#0f1117",
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.06)",
          },
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            color: "rgba(255,255,255,0.7)",
            background: "rgba(255,255,255,0.06)",
            zIndex: 10,
            "&:hover": { background: "rgba(255,255,255,0.14)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            "&::-webkit-scrollbar": { width: "8px", background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.2)",
              borderRadius: "4px",
            },
          }}
        >
          <Box sx={{ p: { xs: 2.5, md: 4 } }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: { xs: "22px", md: "28px" },
                  letterSpacing: "-0.5px",
                }}
              >
                {t('streak.dynamicStreakDialogTitle')}
              </Typography>
              <Typography sx={{ color: "#8b93a7", mt: 0.5, fontSize: "14px" }}>
                {t('streak.dynamicStreakDialogSubtitle')}
              </Typography>
            </Box>

            {!selected ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: "#8b93a7" }}>
                  {t('streak.dynamicStreakLoadingPlans')}
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 2, md: 3 },
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  {/* ---------- Left: vertical plan list ---------- */}
                  <Box
                    sx={{
                      flex: { md: "0 0 320px" },
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {streakPlans.map((plan) => {
                      const active = plan.id === selectedId;
                      return (
                        <Box
                          key={plan.id}
                          onClick={() => setSelectedId(plan.id)}
                          sx={{
                            cursor: "pointer",
                            p: 2,
                            borderRadius: 3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background: active
                              ? "linear-gradient(135deg, rgba(250,17,79,0.18), rgba(78,236,205,0.12))"
                              : "rgba(255,255,255,0.03)",
                            border: active
                              ? "1.5px solid #fa114f"
                              : "1.5px solid rgba(255,255,255,0.07)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: active
                                ? "#fa114f"
                                : "rgba(255,255,255,0.2)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Box>
                            <Box
                              sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                              <Typography
                                sx={{ color: "#fff", fontWeight: 700, fontSize: "16px" }}
                              >
                                {plan.label}
                              </Typography>
                              {plan.badge && (
                                <Chip
                                  label={plan.badge}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    color: "#0f1117",
                                    background:
                                      "linear-gradient(135deg, #4eeccd, #2bd1ad)",
                                  }}
                                />
                              )}
                            </Box>
                            <Typography
                              sx={{ color: "#8b93a7", fontSize: "12px", mt: 0.25 }}
                            >
                              {plan.tagline}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              border: active
                                ? "6px solid #fa114f"
                                : "2px solid rgba(255,255,255,0.25)",
                              transition: "all 0.2s ease",
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>

                  {/* ---------- Right: pricing / rewards sidebar ---------- */}
                  <Box
                    sx={{
                      flex: 1,
                      position: "relative",
                      borderRadius: 3,
                      p: { xs: 2.5, md: 3 },
                      background:
                        "linear-gradient(160deg, #1a1f2e 0%, #11141c 60%, #0d1018 100%)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      boxShadow:
                        "0 20px 50px -20px rgba(250,17,79,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Decorative glows */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: -90,
                        right: -70,
                        width: 220,
                        height: 220,
                        borderRadius: "50%",
                        background:
                          "radial-gradient(circle, rgba(78,236,205,0.35), transparent 70%)",
                        filter: "blur(8px)",
                        pointerEvents: "none",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -110,
                        left: -60,
                        width: 240,
                        height: 240,
                        borderRadius: "50%",
                        background:
                          "radial-gradient(circle, rgba(250,17,79,0.28), transparent 70%)",
                        filter: "blur(10px)",
                        pointerEvents: "none",
                      }}
                    />

                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                        >
                          <Typography
                            sx={{
                              color: "#8b93a7",
                              fontSize: "13px",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                            }}
                          >
                            {selected.label}
                          </Typography>
                          {selected.badge && (
                            <Chip
                              label={selected.badge}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "10px",
                                fontWeight: 800,
                                color: "#0f1117",
                                background:
                                  "linear-gradient(135deg, #4eeccd, #2bd1ad)",
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: { xs: "34px", md: "44px" },
                            lineHeight: 1.05,
                            background:
                              "linear-gradient(135deg, #ffffff 0%, #9d6bff 50%, #4eeccd 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          {selected.days}
                          <Typography
                            component="span"
                            sx={{
                              color: "#8b93a7",
                              fontSize: "16px",
                              ml: 0.75,
                              WebkitTextFillColor: "#8b93a7",
                            }}
                          >
                            {t('streak.dynamicStreakDays')}
                          </Typography>
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            "linear-gradient(135deg, rgba(78,236,205,0.25), rgba(157,107,255,0.25))",
                          border: "1px solid rgba(255,255,255,0.12)",
                          boxShadow: "0 0 24px rgba(78,236,205,0.35)",
                        }}
                      >
                        <BoltRoundedIcon sx={{ color: "#4eeccd", fontSize: 32 }} />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2.5, borderColor: "rgba(255,255,255,0.08)" }} />

                    {/* Rewards grid */}
                    <Grid container spacing={2} sx={{ position: "relative" }}>
                      {[
                        { label: t('streak.dynamicStreakCash'), value: `${selected.sc}`, color: "#ff7a00", glow: "rgba(255,122,0,0.25)" },
                        { label: t('streak.dynamicStreakBonus'), value: `${formatNumber(selected.gc)}`, color: "#ffd54a", glow: "rgba(255,213,74,0.22)" },
                        { label: t('streak.dynamicStreakFreeSpins'), value: `${selected.spins}`, color: "#9d6bff", glow: "rgba(157,107,255,0.25)" },
                      ].map((r) => (
                        <Grid item xs={4} key={r.label}>
                          <Box
                            sx={{
                              textAlign: "center",
                              p: { xs: 1.25, md: 2 },
                              borderRadius: 2.5,
                              background:
                                "linear-gradient(160deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))",
                              border: "1px solid rgba(255,255,255,0.08)",
                              height: "100%",
                              transition: "all 0.25s ease",
                              "&:hover": {
                                transform: "translateY(-3px)",
                                boxShadow: `0 10px 24px -8px ${r.glow}`,
                                borderColor: r.color,
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                color: r.color,
                                fontWeight: 800,
                                fontSize: { xs: "15px", md: "20px" },
                                textShadow: `0 0 18px ${r.glow}`,
                              }}
                            >
                              {r.value}
                            </Typography>
                            <Typography
                              sx={{ color: "#8b93a7", fontSize: "11px", mt: 0.5 }}
                            >
                              {r.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Button
                      fullWidth
                      endIcon={<BoltRoundedIcon />}
                      disabled={submitting}
                      onClick={handleSelectStreak}
                      sx={{
                        position: "relative",
                        mt: 3,
                        py: 1.4,
                        borderRadius: 2.5,
                        fontWeight: 800,
                        fontSize: "15px",
                        textTransform: "none",
                        color: "#fff",
                        background: "linear-gradient(135deg, #fa114f, #c4003a)",
                        boxShadow: "0 12px 28px -10px rgba(250,17,79,0.7)",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          background: "linear-gradient(135deg, #ff2f64, #d40040)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 16px 34px -10px rgba(250,17,79,0.85)",
                        },
                      }}
                    >
                      {submitting
                        ? t('streak.dynamicStreakStarting')
                        : t('streak.dynamicStreakStartCta', { days: selected.days })}
                    </Button>
                  </Box>
                </Box>

                {/* ---------- Features & T&C (side by side) ---------- */}
                <Box
                  sx={{
                    mt: 3,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 3,
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}
                >
                  <Grid container spacing={3}>
                    {/* Left: Specifications/Features */}
                    <Grid item xs={12} md={selected.termsAndConditions ? 7 : 12}>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "16px",
                          mb: 2,
                        }}
                      >
                        {t('streak.dynamicStreakWhatsIncluded', {
                          plan: selected.label,
                        })}
                      </Typography>
                      <Grid container spacing={1.5}>
                        {selected.features.map((f) => (
                          <Grid item xs={12} key={f}>
                            <Box
                              sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                            >
                              <CheckCircleRoundedIcon
                                sx={{ color: "#4eeccd", fontSize: 20 }}
                              />
                              <Typography sx={{ color: "#cfd4e0", fontSize: "14px" }}>
                                {f}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* Right: Terms and Conditions (half) */}
                    {selected.termsAndConditions && (
                      <Grid item xs={12} md={5}>
                        <Box
                          sx={{
                            pl: { md: 2.5 },
                            borderLeft: { md: "1px solid rgba(255,255,255,0.07)" },
                            pt: { xs: 2.5, md: 0 },
                            borderTop: { xs: "1px solid rgba(255,255,255,0.07)", md: "none" },
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#8b93a7",
                              fontWeight: 600,
                              fontSize: "13px",
                              mb: 1,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {t('streak.dynamicStreakTerms')}
                          </Typography>
                          <Typography sx={{ color: "#cfd4e0", fontSize: "13px", lineHeight: 1.6 }}>
                            {selected.termsAndConditions}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DynamicStreak;
